import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import SearchWidget from './SearchWidget';
import Icon, { StarIcon } from './Icons';
import { SmartImg } from './Layout';
import { offers } from '../data/common';

export function useQuery() {
  const [params] = useSearchParams();
  return Object.fromEntries(params.entries());
}

// Remount a page whenever its query string changes so local filter state resets.
export function Keyed({ page: Page }) {
  const { search } = useLocation();
  return <Page key={search} />;
}

/** Landing ("search") page header for a module: big search card over a photo. */
export function ModuleLanding({ tab, title, subtitle, image, children }) {
  return (
    <>
      <section className="module-hero" style={{ '--hero-img': `url(${image})` }}>
        <div className="container">
          <div className="module-hero-text">
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
          <SearchWidget initialTab={tab} hideTabs />
        </div>
      </section>
      <div className="module-landing-body">{children}</div>
    </>
  );
}

export function ModuleOffers({ module }) {
  const list = offers.filter((o) => o.module === module);
  return (
    <div className="offer-grid">
      {list.map((o) => (
        <div key={o.code + o.title} className="offer-card">
          <div className="offer-img"><SmartImg src={o.image} alt={o.category} /></div>
          <div className="offer-body">
            <span className="offer-cat">{o.category.toUpperCase()}</span>
            <h3>{o.title}</h3>
            <p>{o.desc}</p>
            <div className="offer-foot"><span className="coupon">Code: <b>{o.code}</b></span></div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Faq({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="faq">
      {items.map(([q, a], i) => (
        <div key={q} className={`faq-item ${open === i ? 'open' : ''}`}>
          <button type="button" onClick={() => setOpen(open === i ? -1 : i)}>
            <span>{q}</span><Icon name="down" size={16} />
          </button>
          {open === i && <p>{a}</p>}
        </div>
      ))}
    </div>
  );
}

export function SectionHead({ title, sub, link }) {
  return (
    <div className="section-head">
      <div><h2>{title}</h2>{sub && <p className="muted">{sub}</p>}</div>
      {link && <Link to={link.to} className="link">{link.label} <Icon name="arrow" size={14} /></Link>}
    </div>
  );
}

export function Counter({ label, hint, value, min, max, onChange }) {
  return (
    <div className="counter">
      <div><b>{label}</b>{hint && <span>{hint}</span>}</div>
      <div className="counter-ctl">
        <button type="button" disabled={value <= min} onClick={() => onChange(value - 1)}>−</button>
        <span>{value}</span>
        <button type="button" disabled={value >= max} onClick={() => onChange(value + 1)}>+</button>
      </div>
    </div>
  );
}

/** Collapsed search: shows the current search as [label, value] pairs with a Modify Search button. */
function SearchSummary({ items, tag, onModify }) {
  return (
    <div className="search-summary">
      {tag && <span className="search-summary-tag">{tag}</span>}
      <dl className="search-summary-items">
        {items.map(([label, value]) => (
          <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
        ))}
      </dl>
      <button type="button" className="btn search-summary-btn" onClick={onModify}>
        <Icon name="search" size={16} /> Modify Search
      </button>
    </div>
  );
}

export function PageHero({ tab, title, subtitle, query, summary }) {
  const [editing, setEditing] = useState(!summary);
  return (
    <section className="page-hero">
      <div className="page-hero-band">
        <div className="container">
          {editing ? (
            <>
              {summary && (
                <div className="search-edit-head">
                  <b>Modify your search</b>
                  <button type="button" className="search-edit-close" aria-label="Close modify search" onClick={() => setEditing(false)}>
                    <Icon name="close" size={18} />
                  </button>
                </div>
              )}
              <SearchWidget key={JSON.stringify(query)} initialTab={tab} initialValues={query} compact hideTabs />
            </>
          ) : (
            <SearchSummary items={summary.items} tag={summary.tag} onModify={() => setEditing(true)} />
          )}
        </div>
      </div>
      <div className="container">
        <div className="page-hero-text">
          <div className="crumbs"><Link to="/">Home</Link> <Icon name="right" size={12} /> <span>{title}</span></div>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
    </section>
  );
}

export function Stars({ n }) {
  return (
    <span className="stars" aria-label={`${n} star`}>
      {[1, 2, 3, 4, 5].map((i) => <StarIcon key={i} size={13} filled={i <= n} />)}
    </span>
  );
}

const ratingWord = (v) => (v >= 4.7 ? 'Exceptional' : v >= 4.4 ? 'Excellent' : v >= 4 ? 'Very Good' : 'Good');

export function Rating({ value, reviews, compact }) {
  if (value == null) return compact ? null : <span className="rating"><span className="tag">New</span></span>;
  return (
    <span className="rating">
      <span className="rating-badge">{value.toFixed(1)}</span>
      {!compact && <span className="rating-word">{ratingWord(value)}</span>}
      {reviews != null && <span className="muted small">({reviews.toLocaleString('en-IN')} ratings)</span>}
    </span>
  );
}

export function SortBar({ count, label, sort, setSort, options }) {
  return (
    <div className="sort-bar">
      <span className="sort-count">Showing <b>{count}</b> {label}</span>
      <div className="sort-tabs">
        <span className="muted small">Sort by</span>
        {options.map(([k, l]) => (
          <button key={k} className={`sort-tab ${sort === k ? 'active' : ''}`} onClick={() => setSort(k)}>{l}</button>
        ))}
      </div>
    </div>
  );
}

export function FilterGroup({ title, children }) {
  return (
    <div className="filter-group">
      <h4>{title}</h4>
      {children}
    </div>
  );
}

export function Check({ label, checked, onChange, extra }) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
      {extra && <span className="muted small check-extra">{extra}</span>}
    </label>
  );
}

export const toggleIn = (arr, val, on) => (on ? [...arr, val] : arr.filter((x) => x !== val));

export function Empty({ text = 'No results match your filters.' }) {
  return (
    <div className="empty">
      <span className="empty-icon"><Icon name="compass" size={36} /></span>
      <p>{text}</p>
    </div>
  );
}
